// src/modules/calculators/CalculatorsPage.jsx
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Thermometer, Zap, Volume2 } from "lucide-react";
import HeatStress from "./HeatStress";
import NoiseCalculator from "./NoiseCalculator";
import LightingCalculator from "./LightingCalculator";
import { useCmsDoc } from "../../hooks/useCmsDoc";
import {
  CALCULATORS_CMS_DOC,
  defaultCalculatorsCms,
  mergeCalculatorsCms,
} from "../../lib/calculatorsCms";

const CalculatorsPage = () => {
  const [activeTab, setActiveTab] = useState("calor");
  const { data } = useCmsDoc(
    CALCULATORS_CMS_DOC.collection,
    CALCULATORS_CMS_DOC.id,
    defaultCalculatorsCms()
  );
  const cms = useMemo(() => mergeCalculatorsCms(data), [data]);

  const tabs = [
    { id: "calor", label: "Estrés Térmico", short: "Calor", icon: Thermometer },
    { id: "ruido", label: "Ruido", short: "Ruido", icon: Volume2 },
    { id: "iluminacion", label: "Iluminación", short: "Luz", icon: Zap },
  ];

  return (
    <div className="min-h-screen bg-surface pt-20 pb-8 md:pt-24 md:pb-12">
      <div className="container mx-auto px-4 sm:px-6">
        <header className="mb-4 md:mb-8">
          <h1 className="font-display text-2xl leading-tight font-black text-brand-dark tracking-tighter sm:text-3xl md:text-4xl">
            {cms.page.title}{" "}
            <span className="text-brand-primary">{cms.page.titleHighlight}</span>
          </h1>
          <p className="mt-1 text-sm text-gray-500 font-medium max-w-xl md:text-base">
            {cms.page.subtitle}
          </p>
        </header>

        <div className="sticky top-[var(--nav-h)] z-40 -mx-4 mb-4 border-b border-border/60 bg-surface/95 px-4 py-2 backdrop-blur-md sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 md:static md:mb-8 md:backdrop-blur-none">
          <div
            className="flex gap-1.5 overflow-x-auto overscroll-x-contain rounded-2xl bg-gray-100 p-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:w-fit sm:gap-2 sm:rounded-[24px] sm:p-1.5 [&::-webkit-scrollbar]:hidden"
            role="tablist"
            aria-label="Selector de calculadora"
          >
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold transition-all cursor-pointer sm:gap-2.5 sm:rounded-[20px] sm:px-6 sm:py-3.5 sm:text-sm ${
                    active
                      ? "bg-white text-brand-primary shadow-md"
                      : "text-gray-400 hover:text-brand-dark"
                  }`}
                >
                  <Icon size={16} className="shrink-0 sm:hidden" />
                  <Icon size={18} className="hidden shrink-0 sm:block" />
                  <span className="sm:hidden">{tab.short}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="rounded-2xl border border-border bg-white p-4 shadow-xl sm:rounded-[32px] sm:p-6 md:rounded-[48px] md:p-10 md:shadow-2xl"
        >
          {activeTab === "calor" && <HeatStress cms={cms} />}
          {activeTab === "ruido" && <NoiseCalculator cms={cms} />}
          {activeTab === "iluminacion" && <LightingCalculator />}
        </motion.div>
      </div>
    </div>
  );
};

export default CalculatorsPage;
